import { CHART_BUCKETS_RECENT_START_INDEX, CHART_LABELS, CHART_LABELS_MOBILE } from '../../../../dashboard/overdue-statistic/overdueChartConstants';
import {
	DEFAULT_OVERDUE_CHART_CONFIG,
	normalizeOverdueChartConfig,
	OVERDUE_CHART_DIMENSION,
	OVERDUE_CHART_METRIC,
	OVERDUE_CHART_RANGE_MODE,
	getOverdueChartDimensionLabel,
	getOverdueChartMetricLabel,
	getOverdueChartRangeModeLabel,
} from './overdueChartConfig';

export {
	DEFAULT_OVERDUE_CHART_CONFIG,
	normalizeOverdueChartConfig,
	OVERDUE_CHART_RANGE_MODE,
	OVERDUE_CHART_RANGE_MODE_OPTIONS,
	getOverdueChartRangeModeLabel,
} from './overdueChartConfig';

const OVERDUE_DATA_KEYS = {
	delivery: [
		'totalDeliveryOverdue_30_orders',
		'totalDeliveryOverdue_10_30_orders',
		'totalDeliveryOverdue_0_10_orders',
		'totalDelivery_0_10_orders',
		'totalDelivery_10_30_orders',
		'totalDelivery_30_60_orders',
		'totalDelivery_60_90_orders',
		'totalDelivery_90_180_orders',
		'totalDelivery_180_360_orders',
		'totalDelivery_360_plus_orders',
	],
	accept: [
		'totalAcceptOverdue_30_orders',
		'totalAcceptOverdue_10_30_orders',
		'totalAcceptOverdue_0_10_orders',
		'totalAccept_0_10_orders',
		'totalAccept_10_30_orders',
		'totalAccept_30_60_orders',
		'totalAccept_60_90_orders',
		'totalAccept_90_180_orders',
		'totalAccept_180_360_orders',
		'totalAccept_360_plus_orders',
	],
};

export const OVERDUE_STAT_BUCKET_KEYS = OVERDUE_DATA_KEYS;

const PAYMENT_TYPE_LABELS = {
	delivery: '发货尾款',
	accept: '验收尾款',
};

const OVERDUE_CUSTOMER_MAX_SLICES = 10;

export const OVERDUE_RANK_OTHER_MEMBER_START_RANK = OVERDUE_CUSTOMER_MAX_SLICES;

export const findOverdueRankItemByKey = (items = [], key = null) => {
	if (!key) return null;
	const direct = items.find((row) => row.key === key);
	if (direct) return direct;
	const otherRow = items.find((row) => row.key === '__other__');
	return otherRow?.members?.find((member) => member.key === key) ?? null;
};

export const resolveOverdueRankHighlightKey = (items = [], selectedKey = null) => {
	if (!selectedKey) return null;
	if (items.some((row) => row.key === selectedKey)) return selectedKey;
	const otherRow = items.find((row) => row.key === '__other__');
	if (otherRow?.members?.some((member) => member.key === selectedKey)) return '__other__';
	return selectedKey;
};

const getActiveBucketKeys = (rangeMode) => {
	const overdueEndIndex = CHART_BUCKETS_RECENT_START_INDEX;
	const deliveryKeys =
		rangeMode === OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY
			? OVERDUE_DATA_KEYS.delivery.slice(0, overdueEndIndex)
			: OVERDUE_DATA_KEYS.delivery;
	const acceptKeys =
		rangeMode === OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY
			? OVERDUE_DATA_KEYS.accept.slice(0, overdueEndIndex)
			: OVERDUE_DATA_KEYS.accept;
	return [...deliveryKeys, ...acceptKeys];
};

const resolveOverdueEntryKey = (item) =>
	item.ar_id?.toString() || `${item.order_id}_${item.scheduleNode_id}_${item.title}_${item.overdueDay}`;

const collectOverdueEntries = (overdueData = {}, rangeMode) => {
	const seen = new Set();
	const entries = [];

	for (const key of getActiveBucketKeys(rangeMode)) {
		const orderids = overdueData?.[key]?.orderids;
		if (!Array.isArray(orderids)) continue;
		for (const item of orderids) {
			const uniqueKey = resolveOverdueEntryKey(item);
			if (seen.has(uniqueKey)) continue;
			seen.add(uniqueKey);
			entries.push(item);
		}
	}

	return entries;
};

const collapseOverdueRankRows = (rows, metric, entityUnit = '家') => {
	if (rows.length <= OVERDUE_CUSTOMER_MAX_SLICES) return rows;

	const sorted = [...rows].sort(
		(a, b) =>
			(metric === OVERDUE_CHART_METRIC.AR_AMOUNT ? b.arAmount - a.arAmount : b.orderIds.size - a.orderIds.size) ||
			a.label.localeCompare(b.label, 'zh-CN'),
	);
	const head = sorted.slice(0, OVERDUE_CUSTOMER_MAX_SLICES - 1);
	const tail = sorted.slice(OVERDUE_CUSTOMER_MAX_SLICES - 1);
	const otherOrderIds = new Set();
	let otherArAmount = 0;
	let otherOverdueArAmount = 0;

	for (const row of tail) {
		otherArAmount += row.arAmount;
		otherOverdueArAmount += row.overdueArAmount || 0;
		row.orderIds.forEach((orderId) => otherOrderIds.add(orderId));
	}

	if (otherArAmount <= 0 && otherOrderIds.size === 0) return head;

	const memberLabels = tail.map((row) => row.label).filter(Boolean);
	const members = tail.map((row) => ({
		key: row.key,
		label: row.label,
		arAmount: row.arAmount,
		overdueArAmount: row.overdueArAmount || 0,
		orderCount: row.orderIds.size,
		orderIds: [...row.orderIds],
	}));

	return [
		...head,
		{
			key: '__other__',
			label: memberLabels.length > 0 ? `其他（${memberLabels.length} ${entityUnit}）` : '其他',
			arAmount: otherArAmount,
			overdueArAmount: otherOverdueArAmount,
			orderIds: otherOrderIds,
			memberLabels,
			members,
		},
	];
};

/** 具名排行项按指标降序；「其他」汇总项固定末位，不参与中间插队 */
const sortOverdueRankItems = (items) => {
	const otherRow = items.find((row) => row.key === '__other__');
	const ranked = items
		.filter((row) => row.key !== '__other__')
		.sort((a, b) => b.value - a.value || a.label.localeCompare(b.label, 'zh-CN'));
	return otherRow ? [...ranked, otherRow] : ranked;
};

const buildOverdueEntityRankRows = (overdueData = {}, config, { getEntityKey, getEntityLabel, entityUnit }) => {
	const buckets = new Map();

	for (const item of collectOverdueEntries(overdueData, config.rangeMode)) {
		const entityKey = getEntityKey(item);
		const label = getEntityLabel(item);
		const prev = buckets.get(entityKey) ?? { key: entityKey, label, arAmount: 0, overdueArAmount: 0, orderIds: new Set() };
		prev.label = label;
		const arAmount = Number(item.accountsReceivable) || 0;
		prev.arAmount += arAmount;
		if (Number(item.overdueDay) < 0) {
			prev.overdueArAmount += arAmount;
		}
		const orderId = item.order_id?.toString() || item.orderId?.toString();
		if (orderId) prev.orderIds.add(orderId);
		buckets.set(entityKey, prev);
	}

	const currencyMetric = config.metric === OVERDUE_CHART_METRIC.AR_AMOUNT;
	const collapsedRows = collapseOverdueRankRows([...buckets.values()], config.metric, entityUnit);
	const items = sortOverdueRankItems(
		collapsedRows
			.map((row) => ({
				key: row.key,
				label: row.label,
				value: currencyMetric ? row.arAmount : row.orderIds.size,
				arAmount: row.arAmount,
				overdueArAmount: row.overdueArAmount,
				orderCount: row.orderIds.size,
				orderIds: [...row.orderIds],
				memberLabels: row.memberLabels ?? null,
				members: row.members?.map((member) => ({
					...member,
					value: currencyMetric ? member.arAmount : member.orderCount,
					orderIds: Array.isArray(member.orderIds) ? member.orderIds : [],
				})) ?? null,
			}))
			.filter((row) => row.value > 0),
	);

	const totalValue = items.reduce((sum, row) => sum + row.value, 0);
	return { config, items, grouped: null, totalValue, currencyMetric };
};

const buildOverdueCustomerRows = (overdueData = {}, config) =>
	buildOverdueEntityRankRows(overdueData, config, {
		getEntityKey: (item) => item.customerId?.toString() || item.customerName?.trim() || '__unknown__',
		getEntityLabel: (item) => item.customerName?.trim() || '未知客户',
		entityUnit: '家',
	});

const buildOverdueSaleUserRows = (overdueData = {}, config) =>
	buildOverdueEntityRankRows(overdueData, config, {
		getEntityKey: (item) => item.saleUserName?.trim() || '__unknown__',
		getEntityLabel: (item) => item.saleUserName?.trim() || '未知销售员',
		entityUnit: '人',
	});

const getUniqueOrderCount = (orderids) => {
	if (!orderids || !Array.isArray(orderids) || orderids.length === 0) return 0;
	const uniqueOrderIds = new Set(orderids.map((item) => item.order_id?.toString() || item.orderId).filter(Boolean));
	return uniqueOrderIds.size;
};

const pickBucketValue = (overdueData = {}, key, metric) => {
	const bucket = overdueData?.[key];
	if (metric === OVERDUE_CHART_METRIC.ORDER_COUNT) {
		if (Array.isArray(bucket?.orderids) && bucket.orderids.length > 0) {
			return getUniqueOrderCount(bucket.orderids);
		}
		return Number(bucket?.orderCount) || 0;
	}
	return bucket?.totalAR || 0;
};

const pickBucketArAmount = (overdueData = {}, deliveryKey, acceptKey) =>
	pickBucketValue(overdueData, deliveryKey, OVERDUE_CHART_METRIC.AR_AMOUNT) +
	pickBucketValue(overdueData, acceptKey, OVERDUE_CHART_METRIC.AR_AMOUNT);

const collectOrderIdsFromBuckets = (overdueData = {}, keys = []) => {
	const orderIds = new Set();
	for (const key of keys) {
		const orderids = overdueData?.[key]?.orderids;
		if (Array.isArray(orderids) && orderids.length > 0) {
			for (const item of orderids) {
				const orderId = item.order_id?.toString() || item.orderId?.toString();
				if (orderId) orderIds.add(orderId);
			}
		}
	}
	return orderIds;
};

const pickBucketOrderCount = (overdueData = {}, deliveryKey, acceptKey) => {
	const keys = [deliveryKey, acceptKey];
	const fromDetails = collectOrderIdsFromBuckets(overdueData, keys);
	if (fromDetails.size > 0) return fromDetails.size;
	return keys.reduce((sum, key) => sum + (Number(overdueData?.[key]?.orderCount) || 0), 0);
};

const pickPaymentTypeOrderCount = (overdueData = {}, type, rangeMode) => {
	const meta = overdueData?._meta;
	if (rangeMode === OVERDUE_CHART_RANGE_MODE.ALL && meta) {
		if (type === 'delivery' && meta.orderCountDelivery != null) return Number(meta.orderCountDelivery) || 0;
		if (type === 'accept' && meta.orderCountAccept != null) return Number(meta.orderCountAccept) || 0;
	}

	const keys =
		rangeMode === OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY
			? OVERDUE_DATA_KEYS[type].slice(0, CHART_BUCKETS_RECENT_START_INDEX)
			: OVERDUE_DATA_KEYS[type];
	const fromDetails = collectOrderIdsFromBuckets(overdueData, keys);
	if (fromDetails.size > 0) return fromDetails.size;
	return keys.reduce((sum, key) => sum + (Number(overdueData?.[key]?.orderCount) || 0), 0);
};

const resolveOverdueBucketOverdueArAmount = (bucketIndex, arAmount) =>
	bucketIndex < CHART_BUCKETS_RECENT_START_INDEX ? arAmount : 0;

const buildOverdueRowSummaryStats = (row) => {
	if (!row) return null;
	return {
		salesTotal: Number(row.arAmount) || 0,
		overdueTotal: Number(row.overdueArAmount) || 0,
		orderCount: Number(row.orderCount) || 0,
	};
};

const sumArAmountInRange = (overdueData = {}, rangeMode = OVERDUE_CHART_RANGE_MODE.ALL) => {
	const deliveryArData = pickSeriesValues(overdueData, OVERDUE_DATA_KEYS.delivery, OVERDUE_CHART_METRIC.AR_AMOUNT);
	const acceptArData = pickSeriesValues(overdueData, OVERDUE_DATA_KEYS.accept, OVERDUE_CHART_METRIC.AR_AMOUNT);
	const { deliveryData, acceptData } = filterSeriesByRangeMode({
		labels: CHART_LABELS,
		labelsMobile: CHART_LABELS_MOBILE,
		deliveryData: deliveryArData,
		acceptData: acceptArData,
		rangeMode,
	});
	return deliveryData.reduce((sum, value) => sum + value, 0) + acceptData.reduce((sum, value) => sum + value, 0);
};

const sumOverdueArAmount = (overdueData = {}) => {
	const deliveryArData = pickSeriesValues(overdueData, OVERDUE_DATA_KEYS.delivery, OVERDUE_CHART_METRIC.AR_AMOUNT);
	const acceptArData = pickSeriesValues(overdueData, OVERDUE_DATA_KEYS.accept, OVERDUE_CHART_METRIC.AR_AMOUNT);
	return (
		deliveryArData.slice(0, CHART_BUCKETS_RECENT_START_INDEX).reduce((sum, value) => sum + value, 0) +
		acceptArData.slice(0, CHART_BUCKETS_RECENT_START_INDEX).reduce((sum, value) => sum + value, 0)
	);
};

/** 指定尾款类型在区间口径下的去重订单数 */
export const countUniqueOverdueOrdersByPaymentType = (overdueData = {}, paymentType, rangeMode = OVERDUE_CHART_RANGE_MODE.ALL) =>
	pickPaymentTypeOrderCount(overdueData, paymentType, rangeMode);

/** 指定尾款类型全部账龄区间的应收账款合计 */
export const sumArAmountByPaymentType = (overdueData = {}, paymentType) => {
	const keys = OVERDUE_DATA_KEYS[paymentType] ?? [];
	return pickSeriesValues(overdueData, keys, OVERDUE_CHART_METRIC.AR_AMOUNT).reduce((sum, value) => sum + value, 0);
};

/** 当前区间口径下，发货/验收尾款涉及的去重订单数 */
export const countUniqueOverdueOrders = (overdueData = {}, rangeMode = OVERDUE_CHART_RANGE_MODE.ALL) => {
	const meta = overdueData?._meta;
	if (meta) {
		if (rangeMode === OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY && meta.orderCountOverdue != null) {
			return Number(meta.orderCountOverdue) || 0;
		}
		if (rangeMode === OVERDUE_CHART_RANGE_MODE.ALL && meta.orderCountAll != null) {
			return Number(meta.orderCountAll) || 0;
		}
	}

	const orderIds = new Set();
	for (const item of collectOverdueEntries(overdueData, rangeMode)) {
		const orderId = item.order_id?.toString() || item.orderId?.toString();
		if (orderId) orderIds.add(orderId);
	}
	return orderIds.size;
};

/** 超期明细转订单表行：点非零柱即可按 order_id 对齐 */
export const collectOverdueOrdersForTable = (overdueData = {}, rangeMode = OVERDUE_CHART_RANGE_MODE.ALL) => {
	const byId = new Map();
	for (const item of collectOverdueEntries(overdueData, rangeMode)) {
		const id = item.order_id?.toString() || item.orderId?.toString();
		if (!id) continue;
		const arAmount = Number(item.accountsReceivable) || 0;
		const prev = byId.get(id);
		if (prev) {
			prev.totalPrice += arAmount;
			continue;
		}
		byId.set(id, {
			_id: item.order_id?.toString() || id,
			orderId: item.orderId,
			customerName: item.customerName,
			customerInfo: { customerName: item.customerName },
			totalPrice: arAmount,
			saleUserInfo: item.saleUserName ? { name: item.saleUserName } : undefined,
		});
	}
	return [...byId.values()];
};

const pickSeriesValues = (overdueData = {}, keys = [], metric) => keys.map((key) => pickBucketValue(overdueData, key, metric));

const filterSeriesByRangeMode = ({ labels, labelsMobile, deliveryData, acceptData, rangeMode }) => {
	if (rangeMode !== OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY) {
		return { labels, labelsMobile, deliveryData, acceptData };
	}

	const endIndex = CHART_BUCKETS_RECENT_START_INDEX;
	return {
		labels: labels.slice(0, endIndex),
		labelsMobile: labelsMobile.slice(0, endIndex),
		deliveryData: deliveryData.slice(0, endIndex),
		acceptData: acceptData.slice(0, endIndex),
	};
};

export const buildOverdueStatSeries = (overdueData = {}, overdueConfig = DEFAULT_OVERDUE_CHART_CONFIG) => {
	const config = normalizeOverdueChartConfig(overdueConfig);
	const deliveryData = pickSeriesValues(overdueData, OVERDUE_DATA_KEYS.delivery, config.metric);
	const acceptData = pickSeriesValues(overdueData, OVERDUE_DATA_KEYS.accept, config.metric);

	return filterSeriesByRangeMode({
		labels: CHART_LABELS,
		labelsMobile: CHART_LABELS_MOBILE,
		deliveryData,
		acceptData,
		rangeMode: config.rangeMode,
	});
};

export const buildOverdueChartRows = (overdueData = {}, overdueConfig = DEFAULT_OVERDUE_CHART_CONFIG) => {
	const config = normalizeOverdueChartConfig(overdueConfig);
	const currencyMetric = config.metric === OVERDUE_CHART_METRIC.AR_AMOUNT;

	if (config.dimension === OVERDUE_CHART_DIMENSION.CUSTOMER) {
		return buildOverdueCustomerRows(overdueData, config);
	}

	if (config.dimension === OVERDUE_CHART_DIMENSION.SALE_USER) {
		return buildOverdueSaleUserRows(overdueData, config);
	}

	const { labels, deliveryData, acceptData } = buildOverdueStatSeries(overdueData, config);

	if (config.dimension === OVERDUE_CHART_DIMENSION.PAYMENT_TYPE) {
		const deliveryArData = pickSeriesValues(overdueData, OVERDUE_DATA_KEYS.delivery, OVERDUE_CHART_METRIC.AR_AMOUNT);
		const acceptArData = pickSeriesValues(overdueData, OVERDUE_DATA_KEYS.accept, OVERDUE_CHART_METRIC.AR_AMOUNT);
		const filteredDeliveryAr = filterSeriesByRangeMode({
			labels: CHART_LABELS,
			labelsMobile: CHART_LABELS_MOBILE,
			deliveryData: deliveryArData,
			acceptData: acceptArData,
			rangeMode: config.rangeMode,
		});
		const deliveryTotal = deliveryData.reduce((sum, value) => sum + value, 0);
		const acceptTotal = acceptData.reduce((sum, value) => sum + value, 0);
		const deliveryArAmount = filteredDeliveryAr.deliveryData.reduce((sum, value) => sum + value, 0);
		const acceptArAmount = filteredDeliveryAr.acceptData.reduce((sum, value) => sum + value, 0);
		const deliveryOverdueArAmount = deliveryArData.slice(0, CHART_BUCKETS_RECENT_START_INDEX).reduce((sum, value) => sum + value, 0);
		const acceptOverdueArAmount = acceptArData.slice(0, CHART_BUCKETS_RECENT_START_INDEX).reduce((sum, value) => sum + value, 0);
		const deliveryKeys =
			config.rangeMode === OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY
				? OVERDUE_DATA_KEYS.delivery.slice(0, CHART_BUCKETS_RECENT_START_INDEX)
				: OVERDUE_DATA_KEYS.delivery;
		const acceptKeys =
			config.rangeMode === OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY
				? OVERDUE_DATA_KEYS.accept.slice(0, CHART_BUCKETS_RECENT_START_INDEX)
				: OVERDUE_DATA_KEYS.accept;
		const items = [
			{
				key: 'delivery',
				label: PAYMENT_TYPE_LABELS.delivery,
				value: deliveryTotal,
				arAmount: deliveryArAmount,
				overdueArAmount: deliveryOverdueArAmount,
				orderCount: pickPaymentTypeOrderCount(overdueData, 'delivery', config.rangeMode),
				orderIds: [...collectOrderIdsFromBuckets(overdueData, deliveryKeys)],
			},
			{
				key: 'accept',
				label: PAYMENT_TYPE_LABELS.accept,
				value: acceptTotal,
				arAmount: acceptArAmount,
				overdueArAmount: acceptOverdueArAmount,
				orderCount: pickPaymentTypeOrderCount(overdueData, 'accept', config.rangeMode),
				orderIds: [...collectOrderIdsFromBuckets(overdueData, acceptKeys)],
			},
		].filter((row) => row.value > 0);
		const totalValue = items.reduce((sum, row) => sum + row.value, 0);
		return { config, items, grouped: null, totalValue, currencyMetric };
	}

	const items = labels
		.map((label, index) => {
			const globalBucketIndex = index;
			const deliveryKey = OVERDUE_DATA_KEYS.delivery[globalBucketIndex];
			const acceptKey = OVERDUE_DATA_KEYS.accept[globalBucketIndex];
			const arAmount = pickBucketArAmount(overdueData, deliveryKey, acceptKey);
			const orderIds = [...collectOrderIdsFromBuckets(overdueData, [deliveryKey, acceptKey])];
			const orderCount = orderIds.length || pickBucketOrderCount(overdueData, deliveryKey, acceptKey);
			return {
				key: `bucket-${index}`,
				label,
				value: currencyMetric ? deliveryData[index] + acceptData[index] : orderCount,
				deliveryValue: deliveryData[index],
				acceptValue: acceptData[index],
				arAmount,
				overdueArAmount: resolveOverdueBucketOverdueArAmount(globalBucketIndex, arAmount),
				orderCount,
				orderIds,
			};
		})
		.filter((row) => row.value > 0);

	const grouped = {
		labels,
		series: [
			{ key: 'delivery', label: PAYMENT_TYPE_LABELS.delivery, data: deliveryData },
			{ key: 'accept', label: PAYMENT_TYPE_LABELS.accept, data: acceptData },
		],
	};

	const totalValue = items.reduce((sum, row) => sum + row.value, 0);
	return { config, items, grouped, totalValue, currencyMetric };
};

export const hasOverdueChartData = (overdueData = {}, overdueConfig = DEFAULT_OVERDUE_CHART_CONFIG) => {
	const { items, grouped } = buildOverdueChartRows(overdueData, overdueConfig);
	if (items.length > 0) return true;
	return grouped?.series?.some((series) => series.data.some((value) => value > 0)) ?? false;
};

export const buildOverdueChartTitle = (overdueConfig = DEFAULT_OVERDUE_CHART_CONFIG) => {
	const config = normalizeOverdueChartConfig(overdueConfig);
	const parts = [getOverdueChartDimensionLabel(config.dimension), getOverdueChartMetricLabel(config.metric), getOverdueChartRangeModeLabel(config.rangeMode)].filter(
		Boolean,
	);
	return parts.join(' · ') || '超期统计';
};

export const buildOverdueChartConfigKey = (overdueConfig = DEFAULT_OVERDUE_CHART_CONFIG, prefix = 'overdue') => {
	const config = normalizeOverdueChartConfig(overdueConfig);
	return `${prefix}-${config.rangeMode}-${config.dimension}-${config.metric}-${config.chartType}-${config.barOrientation}`;
};

/** 超期图汇总：区间销售总额、超期总额、区间去重订单总数 */
export const buildOverdueSummaryStats = (
	overdueData = {},
	{ selectedRow = null, rangeMode = OVERDUE_CHART_RANGE_MODE.ALL } = {},
) => {
	if (selectedRow) {
		return buildOverdueRowSummaryStats(selectedRow) ?? { salesTotal: 0, overdueTotal: 0, orderCount: 0 };
	}

	return {
		salesTotal: sumArAmountInRange(overdueData, rangeMode),
		overdueTotal: sumOverdueArAmount(overdueData),
		orderCount: countUniqueOverdueOrders(overdueData, rangeMode),
	};
};
